import { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { displayError } from '../../helpers';
import { getAllDeliveryChallans } from '../../services/DeliveryChallanServices';
import { buildListExcelExportHeader } from '../../utils/listExcelExportHeader';
import { exportFormattedExcelReport } from '../../utils/formattedExcelExport';
import {
  TODAY_DC_DETAIL_COLUMNS,
  TODAY_DC_SUMMARY_COLUMNS,
  buildTodayDcSummaryRows,
  buildTodayDcSummaryCards,
  formatTodayDcCellValue,
  mapDeliveryChallanListRow,
} from './todayDcDetailUtils';

function TodayDcDetailModal({
  open,
  onClose,
  cookies,
  listViewConfig,
}) {
  const [viewMode, setViewMode] = useState('details');
  const [exportingExcel, setExportingExcel] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [selectedDateInput, setSelectedDateInput] = useState(() => moment().format('YYYY-MM-DD'));
  const [loadedDateLabel, setLoadedDateLabel] = useState('');

  const selectedDateLabel = useMemo(() => (
    selectedDateInput
      ? moment(selectedDateInput, 'YYYY-MM-DD').format('DD-MM-YYYY')
      : ''
  ), [selectedDateInput]);

  const isDatePending = Boolean(
    selectedDateLabel && selectedDateLabel !== loadedDateLabel,
  );

  const loadRowsForDate = useCallback(async (dateLabel) => {
    setLoading(true);
    const params = {
      from_date: dateLabel,
      upto_date: dateLabel,
    };
    if (listViewConfig?.plant_ids?.length) {
      params.plant_ids = listViewConfig.plant_ids.join(',');
    }
    try {
      const response = await getAllDeliveryChallans(cookies, params);
      if (!response?.delivery_challan_list) {
        throw new Error('Delivery challan list was not returned by the server.');
      }
      setRows(response.delivery_challan_list.map(mapDeliveryChallanListRow));
      setLoadedDateLabel(dateLabel);
    } catch (error) {
      displayError(error.response?.data ?? error.message, 'Failed to load delivery challans');
      setRows([]);
      setLoadedDateLabel('');
    } finally {
      setLoading(false);
    }
  }, [cookies, listViewConfig]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setViewMode('details');
    const todayInput = moment().format('YYYY-MM-DD');
    const todayLabel = moment().format('DD-MM-YYYY');
    setSelectedDateInput(todayInput);
    setRows([]);
    setLoadedDateLabel('');
    loadRowsForDate(todayLabel);
  }, [open, loadRowsForDate]);

  const handleViewClick = (mode) => {
    setViewMode(mode);
    if (!selectedDateLabel) {
      return;
    }
    if (selectedDateLabel !== loadedDateLabel) {
      loadRowsForDate(selectedDateLabel);
    }
  };

  const summaryRows = useMemo(() => buildTodayDcSummaryRows(rows), [rows]);
  const summaryCards = useMemo(() => buildTodayDcSummaryCards(rows), [rows]);
  const activeColumns = viewMode === 'summary'
    ? TODAY_DC_SUMMARY_COLUMNS
    : TODAY_DC_DETAIL_COLUMNS;
  const activeRows = viewMode === 'summary' ? summaryRows : rows;
  const reportTitle = 'List of Delivery Challans';
  const canExport = !loading && !isDatePending && activeRows.length > 0;

  if (!open) {
    return null;
  }

  const handleExcelExport = async () => {
    setExportingExcel(true);
    try {
      const exportHeader = await buildListExcelExportHeader(cookies, listViewConfig?.plant_ids, {
        documentTitle: reportTitle,
      });
      const infoLines = [
        ...exportHeader.infoLines,
        `Date: ${loadedDateLabel}`,
        `View: ${viewMode === 'summary' ? 'Summary' : 'Details'}`,
        `Total Records: ${activeRows.length}`,
      ];
      const exportColumns = activeColumns.map((column) => ({
        key: column.key,
        label: column.label,
        align: column.align === 'end'
          ? 'right'
          : column.align === 'center'
            ? 'center'
            : 'left',
      }));
      const totalKeys = viewMode === 'summary'
        ? ['dc_qty_total', 'amount']
        : ['dc_qty', 'amount'];

      await exportFormattedExcelReport({
        filename: viewMode === 'summary' ? 'today_dc_summary' : 'today_dc_details',
        documentTitle: reportTitle,
        infoLines,
        columns: exportColumns,
        rows: activeRows,
        formatCellValue: formatTodayDcCellValue,
        totalKeys,
        header: {
          companyName: exportHeader.companyName,
          addressLines: exportHeader.addressLines,
          gstNo: exportHeader.gstNo,
          logoDataUrl: exportHeader.logoDataUrl,
        },
      });
    } catch (error) {
      displayError(error?.response?.data ?? error, 'Excel export failed');
    } finally {
      setExportingExcel(false);
    }
  };

  const renderBodyContent = () => {
    if (loading) {
      return <div className="text-center py-4">Loading...</div>;
    }
    if (isDatePending) {
      return (
        <div className="text-center py-4 text-muted">
          Date changed. Click <strong>Details</strong> or <strong>Summary</strong> to load.
        </div>
      );
    }
    if (activeRows.length === 0) {
      return (
        <div className="text-center py-4">
          No delivery challans found for the selected date.
        </div>
      );
    }
    return (
      <div className="table-responsive">
        <table className="table table-bordered customer-site-stats-detail-table">
          <thead>
            <tr>
              {activeColumns.map((column) => (
                <th
                  key={column.key}
                  className={column.align ? `text-${column.align}` : undefined}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {activeRows.map((row, index) => (
              <tr key={row.id || `${row.customer_name}-${row.grade}-${index}`}>
                {activeColumns.map((column) => (
                  <td
                    key={column.key}
                    className={column.align ? `text-${column.align}` : undefined}
                  >
                    {formatTodayDcCellValue(row[column.key], column.key)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="modal customer-site-stats-modal today-dc-detail-modal" tabIndex="-1" style={{ display: 'block' }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header customer-site-stats-modal-header">
            <h5 className="modal-title">{reportTitle}</h5>
            <div className="customer-site-stats-modal-header-actions">
              {canExport && (
                <button
                  type="button"
                  className="btn btn-sm btn-light customer-site-stats-export-btn"
                  title="Download Excel"
                  aria-label="Download Excel"
                  disabled={exportingExcel}
                  onClick={handleExcelExport}
                >
                  <i className="fa fa-file-excel-o text-success" aria-hidden="true" />
                </button>
              )}
              <button type="button" className="close text-white" aria-label="Close" onClick={onClose}>
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
          </div>
          <div className="modal-body">
            <div className="today-dc-date-toolbar">
              <label className="today-dc-date-label" htmlFor="today-dc-date-input">
                Date
              </label>
              <input
                id="today-dc-date-input"
                type="date"
                className="form-control form-control-sm today-dc-date-input"
                value={selectedDateInput}
                onChange={(event) => setSelectedDateInput(event.target.value)}
              />
            </div>
            <div className="today-dc-view-toggle" role="group" aria-label="Today DC view mode">
              <button
                type="button"
                className={`btn btn-sm ${viewMode === 'details' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => handleViewClick('details')}
              >
                Details
              </button>
              <button
                type="button"
                className={`btn btn-sm ${viewMode === 'summary' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => handleViewClick('summary')}
              >
                Summary
              </button>
            </div>
            {!loading && !isDatePending && rows.length > 0 && (
              <div className="today-dc-summary-cards">
                {summaryCards.map((card) => (
                  <div
                    key={card.label}
                    className="today-dc-summary-card"
                    style={{
                      background: card.gradient,
                      boxShadow: card.shadow,
                    }}
                  >
                    <div className="today-dc-summary-card-label">{card.label}</div>
                    <div className="today-dc-summary-card-value">{card.value}</div>
                  </div>
                ))}
              </div>
            )}
            {renderBodyContent()}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TodayDcDetailModal;
